import { Body, breakpoints, Spacer } from '@spritz-finance/ui'
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import styled from 'styled-components'
import {
    ConfirmationDialog,
    ConfirmationDialogMethods,
} from '../../../../../components/ConfirmationDialog'
import { FullAccount } from '../../../../../components/FullAccount'
import { InnerLoading } from '../../../../../components/InnerLoading'
import {
    AccountProvider,
    DirectPaymentQuery,
    FullTokenBalanceFragment,
    PaymentLimits,
} from '../../../../../graphql/generated/graphql'
import { TransactionStatus } from '../../../../../web3/hooks/types'
import { useWeb3Connection } from '../../../../../web3/Web3Provider'
import { useTestPayBillEligible } from '../../linkBill/useTestPay'
import { useDirectPayData } from './useDirectPayData'
import {
    DirectPayState,
    DirectPayStateAction,
    DirectPayStep,
    useDirectPayState,
} from './useDirectPayState'
import { usePaymentTokens } from './usePaymentTokens'

import { getTokenModule } from '../form/tokenModules'
import { BaseTokenModule } from '../form/tokenModules/base'

export type DirectPayment = NonNullable<DirectPaymentQuery['directPayment']>
export type TransactionReceipt = TransactionStatus['receipt'] | null

interface IDirectPayContext extends DirectPayState {
    dispatch: React.Dispatch<DirectPayStateAction>
    closeDialog: () => void
    account: Account
    fullAccount: FullAccount | undefined
    tokenBalances: FullTokenBalanceFragment[]
    title: string
    rewardsBalance: number
    sdkConfig: string[]
    rewardsPaymentAvailable: boolean
    testPaymentAvailable: boolean
    type: DirectPayType
    directPayment: DirectPayment | null

    limits: PaymentLimits | null

    tokenModule: BaseTokenModule | undefined
}

export enum DirectPayType {
    BillPay = 'billPay',
    OffRamp = 'offramp',
    SpritzCard = 'spritzCard',
}

export const DirectPayContext = React.createContext<IDirectPayContext>({} as IDirectPayContext)

export type Account = {
    accountId: string
    provider: AccountProvider
    name: string
    amountDue?: number | null
    ownedByUser?: boolean
}

type DirectPayProviderProps = {
    account: Account
    onClose: () => void
    title?: string
    type?: DirectPayType
    children?: React.ReactNode
    fullAccount?: FullAccount
}

export type DirectPayProviderMethods = {
    handleClose: () => void
}

const LoadingContainer = styled.div`
    position: relative;
    width: 100%;
    min-height: 200px;

    ${breakpoints.xsmall} {
        height: 100vh;
        & > div {
            top: 40%;
        }
    }
`

export const DirectPayProvider = React.forwardRef<DirectPayProviderMethods, DirectPayProviderProps>(
    (
        {
            children,
            account,
            onClose,
            title = 'Bill pay',
            type = DirectPayType.BillPay,
            fullAccount,
        },
        ref
    ) => {
        const { connected } = useWeb3Connection()
        const confirmationDialog = useRef<ConfirmationDialogMethods>(null)

        const { step, dispatch, receipt, selectedToken, directPayment } = useDirectPayState()

        const { loading, allTokenBalances, rewardsBalance, sdkConfig, limits } = useDirectPayData(
            account.accountId,
            type
        )
        const tokenBalances = usePaymentTokens(allTokenBalances)

        const closeDialog = useCallback(() => {
            onClose()
            dispatch({ type: 'reset' })
        }, [dispatch, onClose])

        const handleCloseDialog = useCallback(() => {
            if (step === DirectPayStep.SUMMARY) {
                closeDialog()
            } else {
                confirmationDialog.current?.confirm(closeDialog)
            }
        }, [closeDialog, step])

        useImperativeHandle(ref, () => ({
            handleClose: () => handleCloseDialog(),
        }))

        useEffect(() => {
            if (!connected) closeDialog()
        }, [closeDialog, connected])

        const testPaymentAvailable = useTestPayBillEligible(
            fullAccount?.__typename === 'Bill' ? fullAccount : null
        )
        const cancelAction = 'payment'

        const values = useMemo(() => {
            return {
                step,
                receipt,
                selectedToken,
                directPayment,
                title,
                closeDialog: handleCloseDialog,
                account,
                fullAccount,
                tokenBalances,
                rewardsBalance,
                sdkConfig,
                testPaymentAvailable,
                type,
                dispatch,
                rewardsPaymentAvailable: !testPaymentAvailable && rewardsBalance > 0,
                limits,
                tokenModule: getTokenModule(selectedToken, tokenBalances),
            }
        }, [
            account,
            directPayment,
            dispatch,
            fullAccount,
            handleCloseDialog,
            receipt,
            rewardsBalance,
            sdkConfig,
            selectedToken,
            step,
            testPaymentAvailable,
            title,
            tokenBalances,
            type,
            limits,
        ])

        if (loading)
            return (
                <LoadingContainer>
                    <InnerLoading />
                </LoadingContainer>
            )

        return (
            <DirectPayContext.Provider value={values}>
                {children}
                <ConfirmationDialog ref={confirmationDialog} confirmText={`Cancel ${cancelAction}`}>
                    <Body.Bold size={18}>Cancel {cancelAction}</Body.Bold>
                    <Spacer height={16} />
                    <Body.Med size={14} align="center">
                        Are you sure you want to cancel this {cancelAction}?
                    </Body.Med>
                </ConfirmationDialog>
            </DirectPayContext.Provider>
        )
    }
)

export const useDirectPayContext = () => {
    return React.useContext(DirectPayContext)
}
